/**
 * voicePrefill.js — v166.7.0
 * 
 * Matches voice transcript text against questionnaire options to pre-fill
 * survey answers. Runs client-side after transcription completes.
 * 
 * v166.7.0: Three improvements:
 *   1. Laterality / "side" matching — short options like "Left", "Right", "Both"
 *      now match via dedicated laterality detection (not keyword extraction).
 *   2. Onset/duration matching — SKIP_QUESTION_PATTERNS removed; onset/duration
 *      questions now matched via time-phrase extraction from transcript.
 *   3. Profile-aware prefill — new prefillFromProfile() uses patient demographics
 *      (age, sex, medical history) to pre-fill context/risk-factor questions.
 * 
 * v163.2: Keyword extraction — options like "Gradual, has gotten worse over time"
 * now match on "gradual" alone. Splits on commas, parens, slashes to extract
 * matchable keywords from each option.
 * 
 * Answer key format: `{category}_{questionId}` → [matched options]
 * This mirrors DescribeSection's getAnswerKey pattern.
 */

// Options to never pre-select (negation / exclusion options)
const NONE_OPTIONS = /^(none|no\b|i don't|i do not|not sure|unknown|n\/a|never|no known)/i

// Minimum keyword length to match — prevents false positives on short words
const MIN_KEYWORD_LENGTH = 5

// Common words that appear in transcripts but shouldn't trigger option matches
// NOTE: 'right' and 'left' removed in v166.7.0 — now handled by laterality detector
const NOISE_WORDS = new Set([
  'hearing', 'pain', 'feel', 'feeling', 'have', 'been', 'some', 'also',
  'started', 'start', 'about', 'after', 'before', 'since', 'during',
  'worse', 'better', 'sometimes', 'often', 'usually',
  'doctor', 'noticed', 'notice', 'think', 'know', 'really', 'still',
])

// ─── Laterality Detection (v166.7.0) ───────────────────────────────────────

/** Questions about side/laterality — detected by question text */
const LATERALITY_QUESTION = /which side|which ear|which eye|which knee|which shoulder|which hip|which hand|which foot|which leg|which arm|left or right|affected side|side of|side is/i

/** Laterality indicators in transcript */
const LATERALITY_LEFT  = /\b(left|left[- ]side|left[- ]ear|left[- ]eye|left[- ]knee|left[- ]shoulder|left[- ]hand|left[- ]foot|left[- ]leg|left[- ]arm)\b/i
const LATERALITY_RIGHT = /\b(right[- ]side|right[- ]ear|right[- ]eye|right[- ]knee|right[- ]shoulder|right[- ]hand|right[- ]foot|right[- ]leg|right[- ]arm|my right)\b/i
const LATERALITY_BOTH  = /\b(both|both sides|bilateral|either side|each side)\b/i

/**
 * Try to match a laterality question against transcript.
 * Returns the matching option string from the question's options, or null.
 */
function matchLaterality(question, normalizedTranscript) {
  if (!LATERALITY_QUESTION.test(question.text || '')) return null
  const options = question.options || []

  const hasLeft  = LATERALITY_LEFT.test(normalizedTranscript)
  const hasRight = LATERALITY_RIGHT.test(normalizedTranscript)
  const hasBoth  = LATERALITY_BOTH.test(normalizedTranscript)

  // Both sides mentioned, or explicit "both"
  if ((hasLeft && hasRight) || hasBoth) {
    return options.find(o => /both|bilateral|either/i.test(o)) || null
  }
  if (hasLeft && !hasRight) {
    return options.find(o => /\bleft\b/i.test(o)) || null
  }
  if (hasRight && !hasLeft) {
    return options.find(o => /\bright\b/i.test(o)) || null
  }
  return null
}

// ─── Onset / Duration Matching (v166.7.0) ──────────────────────────────────

/** Questions about when something started or how long it's lasted */
const ONSET_QUESTION = /when did|how long|how many (days|weeks|months|years)|what date|since when|how did it start|how long ago|when was the onset/i

/** Time phrases in transcript → option keywords to match */
const TIME_PHRASES = [
  { pattern: /\b(today|this morning|just now|just started|started today|earlier today|a few hours|couple hours)\b/i, keywords: ['today', 'hours', 'just started', 'less than 24', 'sudden', 'acute'] },
  { pattern: /\b(yesterday|since yesterday|last night|the other day)\b/i, keywords: ['yesterday', '1-2 days', 'day or two', 'couple of days', '24 hours'] },
  { pattern: /\b(few days|couple days|2-3 days|several days|this week|past few days)\b/i, keywords: ['few days', 'days', 'less than a week', 'this week', 'several days', '2-7 days'] },
  { pattern: /\b(a week|one week|about a week|past week|last week)\b/i, keywords: ['1 week', 'one week', 'a week', 'week', '7 days'] },
  { pattern: /\b(two weeks|2 weeks|couple weeks|few weeks|several weeks)\b/i, keywords: ['2 weeks', 'few weeks', 'several weeks', 'weeks', '1-4 weeks'] },
  { pattern: /\b(a month|one month|about a month|past month|last month)\b/i, keywords: ['1 month', 'one month', 'a month', 'month', '4 weeks'] },
  { pattern: /\b(few months|couple months|2-3 months|several months)\b/i, keywords: ['few months', 'months', 'several months', '1-3 months', '1-6 months'] },
  { pattern: /\b(six months|6 months|half a year)\b/i, keywords: ['6 months', 'six months', 'half a year', '3-6 months'] },
  { pattern: /\b(a year|one year|about a year|past year|last year|over a year|for years|several years|many years)\b/i, keywords: ['year', 'years', 'over a year', '12 months', 'more than 6 months', 'chronic', 'long time'] },
  { pattern: /\b(gradual|gradually|slowly|over time|getting worse|progressi)\b/i, keywords: ['gradual', 'gradually', 'progressi', 'over time'] },
  { pattern: /\b(sudden|suddenly|all of a sudden|came on fast|abrupt)\b/i, keywords: ['sudden', 'suddenly', 'abrupt', 'acute', 'all of a sudden'] },
]

/**
 * Try to match an onset/duration question against transcript.
 * Returns the best matching option string, or null.
 */
function matchOnsetDuration(question, normalizedTranscript) {
  if (!ONSET_QUESTION.test(question.text || '')) return null
  const options = question.options || []

  // Find which time phrases appear in transcript
  const matchedKeywords = []
  for (const { pattern, keywords } of TIME_PHRASES) {
    if (pattern.test(normalizedTranscript)) {
      matchedKeywords.push(...keywords)
    }
  }
  if (matchedKeywords.length === 0) return null

  // Score each option by how many keywords it matches
  let bestOption = null
  let bestScore = 0
  for (const option of options) {
    if (NONE_OPTIONS.test(option)) continue
    const optLower = option.toLowerCase()
    let score = 0
    for (const kw of matchedKeywords) {
      if (optLower.includes(kw.toLowerCase())) score++
    }
    if (score > bestScore) {
      bestScore = score
      bestOption = option
    }
  }
  return bestScore > 0 ? bestOption : null
}

// ─── Profile-Aware Prefill (v166.7.0) ──────────────────────────────────────

/** Question patterns that can be answered from profile data
 *  v175.2.0: Broadened to match actual questionnaire patterns —
 *  "Prior history:", "Medical conditions:", "Do you have", "Risk factors:", etc.
 */
const PROFILE_QUESTION_MAP = [
  { pattern: /existing (medical )?conditions|medical history|pre-existing|chronic conditions|diagnosed with|prior history|prior.*history|medical conditions|do you (also )?have|contributing factors|risk factors|do you or family/i, profileKey: 'conditions' },
  { pattern: /current medications|taking any medication|prescription|medications.*currently|currently taking|do you take|do you use.*regularly|current.*medications|prior history and medications/i, profileKey: 'medications' },
  { pattern: /allergi/i, profileKey: 'allergies' },
  { pattern: /do you smoke|tobacco|smoking status|smoker/i, profileKey: 'smoking' },
  { pattern: /alcohol|drink|drinking/i, profileKey: 'alcohol' },
  { pattern: /pregnan/i, profileKey: 'pregnant' },
]

/**
 * Pre-fill questions that can be answered from patient profile data.
 * Merges with (but does not overwrite) voice-transcript-based prefills.
 * 
 * @param {object} profile - Patient profile from profileStore (identification, medicalHistory, etc.)
 * @param {object} questionsByCategory - Same format as prefillFromTranscript
 * @returns {{ answers: object, prefillCount: number }}
 */
// TEST 1.03 — the ONE coercion; see src/utils/profileConditions.js.
import { toConditionList } from './profileConditions'

export function prefillFromProfile(profile, questionsByCategory) {
  if (!profile || !questionsByCategory) return { answers: {}, prefillCount: 0 }

  const medHistory = profile.medicalHistory || {}
  const answers = {}
  let prefillCount = 0

  // Build profile context
  const profileData = {
    // TEST 1.03 — `|| []` substitutes for null and undefined ONLY. The Auto-fill import
    // wrote an OBJECT into medicalHistory.conditions and this line threw on .map.
    conditions: toConditionList(medHistory.conditions).map(c => c.name || c.label || '').filter(Boolean),
    medications: (medHistory.medications || []).map(m => (typeof m === 'string' ? m : m.name || m.label || '')).filter(Boolean),
    allergies: (medHistory.allergies || []).map(a => (typeof a === 'string' ? a : a.name || a.label || '')).filter(Boolean),
    smoking: medHistory.smokingStatus || medHistory.tobacco || '',
    alcohol: medHistory.alcoholUse || medHistory.alcohol || '',
    pregnant: medHistory.pregnant || medHistory.pregnancy || '',
  }

  for (const [category, questions] of Object.entries(questionsByCategory)) {
    if (!Array.isArray(questions)) continue

    for (const question of questions) {
      const { id, type, options } = question
      if (!id || !options || options.length === 0) continue
      if (type === 'scale') continue

      const answerKey = `${category}_${id}`
      const qText = question.text || ''

      for (const { pattern, profileKey } of PROFILE_QUESTION_MAP) {
        if (!pattern.test(qText)) continue

        const data = profileData[profileKey]
        if (!data || (Array.isArray(data) && data.length === 0) || (typeof data === 'string' && !data)) continue

        // Match profile items against question options
        const matches = []
        if (Array.isArray(data)) {
          for (const item of data) {
            const itemLower = item.toLowerCase()
            for (const option of options) {
              if (NONE_OPTIONS.test(option)) continue
              if (option.toLowerCase().includes(itemLower) || itemLower.includes(option.toLowerCase())) {
                if (!matches.includes(option)) matches.push(option)
              }
            }
          }
        } else if (typeof data === 'string') {
          const dataLower = data.toLowerCase()
          for (const option of options) {
            if (NONE_OPTIONS.test(option)) continue
            if (option.toLowerCase().includes(dataLower) || dataLower.includes(option.toLowerCase())) {
              if (!matches.includes(option)) matches.push(option)
            }
          }
        }

        if (matches.length > 0) {
          if (type === 'multi-select') {
            answers[answerKey] = matches
            prefillCount++
          } else if (type === 'single-select' && matches.length === 1) {
            answers[answerKey] = [matches[0]]
            prefillCount++
          }
        }
        break // Only try first matching profile pattern per question
      }
    }
  }

  return { answers, prefillCount }
}

// ─── Core keyword extraction (unchanged from v163.2) ───────────────────────

/**
 * Extract matchable keywords from an option string.
 */
function extractKeywords(optionText) {
  const fragments = optionText
    .replace(/\(([^)]+)\)/g, ', $1')
    .split(/[,/]|\s+[-–—]\s+|\s+or\s+|\s+and\s+/i)
    .map(f => f.trim().toLowerCase())
    .filter(f => f.length >= MIN_KEYWORD_LENGTH)
  
  const keywords = []
  for (const frag of fragments) {
    const words = frag.split(/\s+/).filter(w => w.length >= 3).slice(0, 3).join(' ')
    if (words.length >= MIN_KEYWORD_LENGTH && !NOISE_WORDS.has(words)) {
      keywords.push(words)
    }
    const twoWord = frag.split(/\s+/).filter(w => w.length >= 3).slice(0, 2).join(' ')
    if (twoWord.includes(' ') && twoWord.length >= MIN_KEYWORD_LENGTH && twoWord !== words) {
      keywords.push(twoWord)
    }
    const firstWord = frag.split(/\s+/)[0]
    if (firstWord.length >= 6 && !NOISE_WORDS.has(firstWord) && firstWord !== words) {
      keywords.push(firstWord)
    }
  }
  
  return [...new Set(keywords)]
}

/**
 * Check if any keyword from an option matches in the transcript.
 */
function optionMatchesTranscript(optionText, normalizedTranscript) {
  const keywords = extractKeywords(optionText)
  for (const kw of keywords) {
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const pattern = new RegExp(`\\b${escaped}\\b`, 'i')
    if (pattern.test(normalizedTranscript)) {
      return true
    }
  }
  return false
}

// ─── Main prefill function ─────────────────────────────────────────────────

/**
 * Pre-fill survey answers from voice transcript.
 * 
 * v166.7.0: Now handles laterality and onset/duration questions that were
 * previously skipped. Profile-based prefill is separate (prefillFromProfile).
 * 
 * @param {string} transcript - The voice transcript text
 * @param {object} questionsByCategory - { details: [...], signs_symptoms: [...], context: [...], additional_factors: [...] }
 * @returns {{ answers: object, prefillCount: number }} - Answer map + count of pre-filled questions
 */
export function prefillFromTranscript(transcript, questionsByCategory) {
  if (!transcript || !questionsByCategory) return { answers: {}, prefillCount: 0 }
  
  const normalizedTranscript = transcript.toLowerCase()
  const answers = {}
  let prefillCount = 0
  
  for (const [category, questions] of Object.entries(questionsByCategory)) {
    if (!Array.isArray(questions)) continue
    
    for (const question of questions) {
      const { id, type, options } = question
      if (!id || !options || options.length === 0) continue
      
      // Skip scale questions — too ambiguous
      if (type === 'scale') continue
      
      const answerKey = `${category}_${id}`
      
      // v166.7.0: Try laterality matching first (handles short "Left"/"Right"/"Both" options)
      const lateralityMatch = matchLaterality(question, normalizedTranscript)
      if (lateralityMatch) {
        answers[answerKey] = [lateralityMatch]
        prefillCount++
        continue
      }
      
      // v166.7.0: Try onset/duration matching (handles time-based questions)
      const onsetMatch = matchOnsetDuration(question, normalizedTranscript)
      if (onsetMatch) {
        answers[answerKey] = [onsetMatch]
        prefillCount++
        continue
      }

      // v214.9.1: Try body-region matching for the General specialty's "Where is
      // the problem?" question (multi-select with short labels like "Head & neck",
      // "Pelvic or urinary", "Skin"). The generic keyword matcher below is too
      // weak on these short labels — this matcher uses synonym lists tuned to
      // common patient phrasing ("rash" → Skin, "headache" → Head & neck, etc.).
      const isBodyRegionQuestion = /where.*(in your body|is the problem)/i.test(question.text || '')
      if (isBodyRegionQuestion) {
        const region = detectBodyRegion(normalizedTranscript, options)
        if (region) {
          answers[answerKey] = [region]
          prefillCount++
          continue
        }
      }
      
      // Standard keyword matching for all other questions
      const matches = []
      for (const option of options) {
        if (NONE_OPTIONS.test(option)) continue
        
        if (optionMatchesTranscript(option, normalizedTranscript)) {
          matches.push(option)
        }
      }
      
      if (matches.length === 0) continue
      
      if (type === 'multi-select') {
        answers[answerKey] = matches
        prefillCount++
      } else if (type === 'single-select') {
        if (matches.length === 1) {
          answers[answerKey] = [matches[0]]
          prefillCount++
        }
      }
    }
  }
  
  return { answers, prefillCount }
}

// ─── Body Region Detection (v214.9.0, Bug 2b) ──────────────────────────────
//
// For the General specialty, Q1 ("In a few words, what's bothering you most?")
// is free text. Q3 ("Where is the problem?") asks the patient to pick a body
// region. Rather than make them re-state in two places, we keyword-match Q1's
// text against Q3's options and auto-select the best match. The user can
// override at any time.
//
// Designed to be conservative — if no clear match, return null so we don't
// pre-select something wrong.

/** Body-region keywords mapped to the canonical Q3 option strings.
 *  Matching is case-insensitive and uses substring/word checks on the
 *  patient's free-text. First matching region wins; ordering matters
 *  (more specific terms before more general ones). */
const BODY_REGION_KEYWORDS = [
  // Skin — check before head/neck so "rash on neck" still maps to skin
  { option: 'Skin', patterns: [/\brash\b/i, /\beczema\b/i, /\bhives?\b/i, /\bbreakout\b/i, /\bacne\b/i, /\bpimple/i, /\bbump on (my|the)/i, /\bmole\b/i, /\bitch(y|ing)?\b/i, /\bdry skin\b/i, /\bskin\b/i] },
  // Pelvic / urinary — check before "belly" because "lower abdomen" might overlap
  { option: 'Pelvic or urinary', patterns: [/\burinat/i, /\bpee(ing)?\b/i, /\bUTI\b/i, /\bbladder\b/i, /\bpelvi[cs]\b/i, /\bgroin\b/i, /\bvagina/i, /\bpenis\b/i, /\btestic/i, /\bperiod\b/i, /\bmenstr/i, /\bdischarge\b/i, /\bsex(ual)?\b/i] },
  // Mood / energy / sleep
  { option: 'Mood, energy, or sleep', patterns: [/\banxi/i, /\bdepress/i, /\bmood\b/i, /\bsad\b/i, /\bstress(ed)?\b/i, /\bsleep(ing|y|less)?\b/i, /\binsomnia\b/i, /\btired\b/i, /\bfatigue/i, /\bexhaust/i, /\bno energy\b/i, /\blow energy\b/i, /\bcan'?t sleep\b/i] },
  // Head & neck
  { option: 'Head & neck', patterns: [/\bhead(ache)?\b/i, /\bmigraine/i, /\bdizz/i, /\bvertigo\b/i, /\bear(s|ache)?\b/i, /\bhearing\b/i, /\beye(s)?\b/i, /\bvision\b/i, /\bsight\b/i, /\bnose\b/i, /\bsinus\b/i, /\bthroat\b/i, /\bswallow/i, /\btonsil/i, /\bjaw\b/i, /\bteeth\b/i, /\btooth\b/i, /\bface\b/i, /\bneck\b/i, /\bmouth\b/i, /\blip(s)?\b/i] },
  // Chest
  { option: 'Chest', patterns: [/\bchest\b/i, /\bheart\b/i, /\bpalpitat/i, /\bbreath/i, /\bbreathing\b/i, /\bcough/i, /\blung/i, /\bshort of breath\b/i] },
  // Belly / abdomen
  { option: 'Belly or abdomen', patterns: [/\bstomach\b/i, /\bbelly\b/i, /\babdom/i, /\btummy\b/i, /\bnausea/i, /\bvomit/i, /\bdiarrhea\b/i, /\bconstipat/i, /\bgut\b/i, /\bbowel/i, /\bindigest/i, /\bheartburn\b/i, /\bgas\b/i, /\bbloat/i] },
  // Back or hips
  { option: 'Back or hips', patterns: [/\bback\b/i, /\bhip(s)?\b/i, /\blumbar\b/i, /\bspine\b/i, /\bsciatica\b/i] },
  // Arms or shoulders
  { option: 'Arms or shoulders', patterns: [/\barm(s)?\b/i, /\bshoulder/i, /\belbow/i, /\bwrist/i, /\bhand(s)?\b/i, /\bfinger/i] },
  // Legs or feet
  { option: 'Legs or feet', patterns: [/\bleg(s)?\b/i, /\bknee(s)?\b/i, /\bankle/i, /\bfoot\b/i, /\bfeet\b/i, /\btoe(s)?\b/i, /\bcalf\b/i, /\bcalve(s)?\b/i, /\bthigh/i] },
  // All over
  { option: 'All over', patterns: [/\ball over\b/i, /\beverywhere\b/i, /\bwhole body\b/i, /\bbody aches?\b/i, /\bgeneral(ized)? pain\b/i] },
]

/**
 * Match a free-text complaint description against a list of body region
 * options (Q3 in the General specialty). Returns the best matching option
 * string, or null if no clear match.
 *
 * @param {string} text - patient's free-text complaint (Q1 answer)
 * @param {string[]} options - Q3's option strings to match against
 * @returns {string|null} the matching option, or null if no match
 */
export function detectBodyRegion(text, options) {
  if (!text || typeof text !== 'string' || text.trim().length < 3) return null
  if (!Array.isArray(options) || options.length === 0) return null

  const normalized = text.toLowerCase()

  for (const { option, patterns } of BODY_REGION_KEYWORDS) {
    // Skip if this option isn't in the question's options list (defensive)
    if (!options.includes(option)) continue
    for (const pattern of patterns) {
      if (pattern.test(normalized)) return option
    }
  }
  return null
}

/**
 * Get all questions for a case's specialty/focus area, organized by category.
 * 
 * @param {function} getQuestions - questionnaireStore.getQuestions(specialtyId, focusAreaId, category)
 * @param {string} specialtyId
 * @param {string} focusAreaId
 * @returns {object} { details: [...], signs_symptoms: [...], context: [...], additional_factors: [...] }
 */
export function getAllQuestionsForCase(getQuestions, specialtyId, focusAreaId) {
  const categories = ['details', 'signs_symptoms', 'context', 'additional_factors']
  const result = {}
  for (const cat of categories) {
    const questions = getQuestions(specialtyId, focusAreaId, cat)
    if (questions && questions.length > 0) {
      result[cat] = questions
    }
  }
  return result
}
